import * as React from 'react';
import { forwardRef } from 'react';
import { User as UserIcon } from 'lucide-react';
import { RemoteData, rd } from '@passionware/monads';
import { Skeleton } from 'v5/design-system/Skeleton';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  type AvatarSize
} from 'v5/design-system/Avatar';

/**
 * Abstract user type that contains the minimum fields needed for avatar display.
 * This type is designed to work with various user-like objects across the codebase.
 */
export type AbstractUser = {
  avatarUrl?: string | null;
  name?: string;
  email?: string;
};

export type UserAvatarProps = Omit<
  React.ComponentPropsWithoutRef<typeof Avatar>,
  'size'
> & {
  /**
   * User data as RemoteData. The component will automatically handle loading state
   * and extract avatarUrl, name, and email from the user object.
   *
   * When rendering a user from a list, you can use:
   * ```tsx
   * <UserAvatar user={rd.map(listData, () => user)} />
   * ```
   * This ensures the avatar shows loading state while the list is loading.
   */
  user: RemoteData<AbstractUser>;
  /**
   * Size of the avatar
   */
  size?: AvatarSize;
  /**
   * Custom fallback content (overrides default initials/icon)
   */
  fallback?: React.ReactNode;
};

/**
 * Generates initials from a name
 */
function getInitials(name?: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const UserAvatar = forwardRef<
  React.ElementRef<typeof Avatar>,
  UserAvatarProps
>(({ user, size = 'md', fallback, className, ...rest }, ref) => {
  return (
    // Keyed by the picture: a loaded one's status would otherwise outlive it and hide the initials.
    <Avatar
      key={rd.tryGet(user)?.avatarUrl ?? ''}
      ref={ref}
      size={size}
      className={className}
      {...rest}
    >
      {rd
        .journey(user)
        .wait(
          <AvatarFallback>
            <Skeleton className="w-full h-full rounded-md" />
          </AvatarFallback>
        )
        .catch(() => (
          <AvatarFallback>
            <UserIcon className="text-fg-muted" />
          </AvatarFallback>
        ))
        .map((userData: AbstractUser) => {
          const avatarUrl = userData?.avatarUrl;
          const name = userData?.name;
          const email = userData?.email;

          const initials = getInitials(name || email);
          const displayInitials = initials.slice(0, 2);

          return (
            <>
              {avatarUrl && (
                <AvatarImage
                  src={avatarUrl}
                  alt={name || email || 'User avatar'}
                />
              )}
              <AvatarFallback>
                {fallback ? (
                  fallback
                ) : displayInitials ? (
                  <span className="font-medium text-fg-muted">
                    {displayInitials}
                  </span>
                ) : (
                  <UserIcon className="text-fg-muted" />
                )}
              </AvatarFallback>
            </>
          );
        })}
    </Avatar>
  );
});

UserAvatar.displayName = 'UserAvatar';
